import { FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { platform } from 'network-utils';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from 'translations';
import { ValidateTextField, Tooltip, Icon } from 'learning-panel-components';
import { selectUserUuid } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { getUserInfoKey } from '@queries/platform/login';
import { useGetAccountData, getAccountData } from '@queries/platform/accountSettings';
import styles from './nickname.module.scss';

const NicknameContainer: FC<NicknameContainerProps> = (props: NicknameContainerProps) => {
    const { setShowSuccessNotification, setNotificationMessage, setIsLoading, isLoading, setEmail } = props;
    const services = platform.learningPanel.AccountSettingsAPIs;
    const dispatch = useDispatch();

    const userUuid = useSelector(selectUserUuid);
    const [nickname, setNickname] = useState<string>('');
    const [prevNickname, setPrevNickname] = useState<string>('');
    const [errorNickname, setErrorNickname] = useState<string>('');
    const [fullName, setFullName] = useState<string>('');
    const [prevFullName, setPrevFullName] = useState<string>('');
    const [errorFullName, setErrorFullName] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const [useNicknameDescription, setUseNicknameDescription] = useState<boolean>(false);
    const generalErrorText = translate('generalError');
    const nicknameUpdated = translate('NicknameUpdated');
    const fullNameUpdated = translate('FullNameUpdated');
    const nicknameTaken = translate('NicknameAlreadyTaken');
    const invalidNickname = translate('InvalidNickname');

    useEffect(() => {
        const tenantName = window.location.host.split('.')[0];
        if (tenantName === 'capgemini') {
            setUseNicknameDescription(true);
        }
    }, []);

    const { isLoading: isLoadingUserInfo, data } = useGetAccountData(userUuid);
    const accountData = data?.account_data;

    const updateNickname = async (nickname: string): Promise<{ success: boolean; error?: string }> => {
        const updateNicknameResponse = await services.updateNickname({ nickname });
        return updateNicknameResponse;
    };

    const updateFullName = async (fullName: string): Promise<{ success: boolean; error?: string }> => {
        const updateFullNameResponse = await services.updateFullName({ full_name: fullName });
        return updateFullNameResponse;
    };

    const generalError = () => {
        dispatch(
            showAlert({
                value: generalErrorText,
                status: 'error'
            })
        );
    };

    const handleError = (nickname: string) => {
        switch (nickname) {
            case 'nickname_already_taken':
                return nicknameTaken;
            case 'invalid_nickname':
                return invalidNickname;
            default:
                return generalErrorText;
        }
    };

    const updateNicknameMutation = useMutation(updateNickname, {
        onSuccess: (updateNicknameResponse, variable) => {
            if (updateNicknameResponse?.success) {
                queryClient.setQueryData([getAccountData, userUuid], oldData => ({
                    account_data: {
                        ...(oldData as platform.AccountData).account_data,
                        nickname: variable
                    }
                }));
                queryClient.invalidateQueries([getUserInfoKey, userUuid]);
                setShowSuccessNotification?.(true);
                setNotificationMessage?.(nicknameUpdated);
                setPrevNickname(nickname);
                return;
            }
            setErrorNickname(handleError(updateNicknameResponse?.error || generalErrorText));
        },
        onError: () => {
            generalError();
        }
    });

    const updateFullNameMutation = useMutation(updateFullName, {
        onSuccess: (updateFullNameResponse, variable) => {
            if (updateFullNameResponse?.success) {
                queryClient.invalidateQueries([getUserInfoKey, userUuid]);
                queryClient.invalidateQueries(['account-data', userUuid]);
                setShowSuccessNotification?.(true);
                setNotificationMessage?.(fullNameUpdated);
                if (!variable) setIsEditing(false);
                return;
            }
            setErrorFullName(handleError(updateFullNameResponse?.error || generalErrorText));
        },
        onError: () => {
            generalError();
        }
    });

    useEffect(() => {
        if (accountData) {
            setNickname(accountData.nickname);
            setPrevNickname(accountData.nickname);
            setFullName(accountData.full_name);
            setPrevFullName(accountData.full_name);
            setEmail(accountData.email);
            setIsLoading?.(false);
        }
    }, [data]);

    useEffect(() => {
        setIsLoading?.(isLoadingUserInfo);
    }, [isLoadingUserInfo]);

    const onCancel = () => {
        setErrorNickname('');
        setErrorFullName('');
        setNickname(prevNickname);
        setFullName(prevFullName);
    };

    const changeVisibilityOfInputField = () => {
        setIsEditing(!isEditing);
    };

    return (
        <>
            {isLoading ? undefined : (
                <div className={styles['nickname-and-email']}>
                    <ValidateTextField
                        value={nickname}
                        onChange={(nickname: string) => updateNicknameMutation.mutate(nickname)}
                        error={errorNickname}
                        isLoading={updateNicknameMutation.isLoading}
                        onCancel={onCancel}
                        maxLength={307}
                        fontSize="32px"
                        inputTextSize="xx-large"
                    />
                    {useNicknameDescription && (
                        <div className={styles.autoGenerated}>
                            <span className={styles.text}>{translate('UserNameIsAutoGenerated')}</span>
                        </div>
                    )}
                    {fullName || isEditing ? (
                        <div className={styles.fullName}>
                            <ValidateTextField
                                fontSize="17px"
                                colorFont="rgba(255, 255, 255, 0.7)"
                                editIconHeight="1rem"
                                editIconWidth="1rem"
                                smallMode={true}
                                textFieldMarginRight="0.01rem"
                                iconMarginTops="-0.5rem"
                                inputTextColor="rgba(255, 255, 255, 0.7)"
                                inputTextSize="large"
                                textFieldFontWeight={300}
                                inputNotchedOutlineColor="rgba(255, 255, 255, 0.7)"
                                value={fullName}
                                onChange={(fullName: string) => updateFullNameMutation.mutate(fullName)}
                                error={errorFullName}
                                isLoading={updateFullNameMutation.isLoading}
                                onCancel={onCancel}
                                maxLength={307}
                                isExternalEditing={isEditing}
                                editIconToolTipText={translate('editFullName')}
                            />
                        </div>
                    ) : (
                        <div className={styles.fullNameEmpty} onClick={() => changeVisibilityOfInputField()}>
                            <Icon name="plus" className={styles.icon} />
                            Add full name
                            <Tooltip
                                title={translate('toBeUsedOnScreenLeaderboardInteractions')}
                                placement="right-start"
                                minWidth="20rem"
                            >
                                <span>
                                    <Icon className={styles.infoIcon} name="info" />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export interface NicknameContainerProps {
    setShowSuccessNotification?: Function;
    setNotificationMessage?: Function;
    setIsLoading?: Function;
    isLoading: boolean;
    setEmail: Function;
}

export default NicknameContainer;
